import axios from "axios";
import { getAuthToken, setAuthToken, clearAuthToken } from "./authToken";

// Some Vercel preview deploys ship without REACT_APP_BACKEND_URL set, in
// which case `BACKEND_URL` becomes the string "undefined" and every API
// call resolves against the frontend (404 → broken image icons, login
// loops, etc). Fall back to the hard-coded production hostname so the
// app keeps working even on a misconfigured preview.
//
// 🔁 Transition mai 2026 : on bascule vers api.terouma.fr (custom domain
// Railway). L'ancien chekalim-backend-production.up.railway.app reste
// servi par Railway en parallèle pendant la transition, donc aucun risque
// de casse — c'est juste l'URL "officielle" qui change pour s'aligner sur
// le nouveau domaine terouma.fr.
export const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL
  || "https://api.terouma.fr";
export const API = `${BACKEND_URL}/api`;
// PUBLIC_API is the same base URL — used by public pages (no auth needed)
export const PUBLIC_API = API;

// Adresse ABSOLUE d'un fichier servi par l'API à partir du chemin qu'elle
// renvoie (ex. image de campagne « /api/campaigns/<id>/image.png?t=… ») :
// le site (terouma.fr) et l'API (api.terouma.fr) sont deux domaines.
// ⚠ Ne jamais écrire PUBLIC_API.replace("/api", "") : avec
// « https://api.terouma.fr/api », c'est le « /api » de « //api.terouma.fr »
// qui saute → « https:/.terouma.fr/api/… », image cassée.
export const urlApi = (chemin) => {
  if (!chemin) return null;
  if (/^https?:\/\//i.test(chemin)) return chemin;
  return `${BACKEND_URL}${chemin.startsWith("/") ? "" : "/"}${chemin}`;
};

const IMPERSONATE_KEY = "terouma_impersonate_org";

// ── Jeton de connexion (repli Bearer pour iPhone) ───────────────────────────
// Le stockage du jeton (sessionStorage + copie persistante en localStorage,
// pour que la web app installée ne redemande plus le mot de passe à chaque
// ouverture) vit dans lib/authToken.js — pourquoi et garde-fous y sont
// détaillés. Réexporté ici : les écrans l'importent depuis lib/api.
export { getAuthToken, setAuthToken, clearAuthToken };

export const getImpersonatedOrgId = () =>
  typeof window !== "undefined"
    ? window.localStorage.getItem(IMPERSONATE_KEY) || null
    : null;

export const setImpersonatedOrgId = (orgId) => {
  if (typeof window === "undefined") return;
  if (orgId) {
    window.localStorage.setItem(IMPERSONATE_KEY, orgId);
  } else {
    window.localStorage.removeItem(IMPERSONATE_KEY);
  }
};

export const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30000, // 30s — Railway cold start peut prendre 10-15s
});

apiClient.interceptors.request.use((config) => {
  // Bearer fallback — required for iOS Safari (ITP drops cross-site cookies).
  // The cookie path is still tried first by the browser via withCredentials,
  // and the backend accepts whichever arrives.
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  // Owner impersonation: inject organization_id query param so backend
  // scope.py treats this request as scoped to that org. Skip for /owner/*
  // routes which manage org scope through path params themselves.
  // ALSO skip /public/* and /campaigns/public/* routes — they're public
  // endpoints (no auth), and adding organization_id there filters the
  // result unexpectedly (e.g. landing page only seeing one org's campaigns).
  const impersonate = getImpersonatedOrgId();
  const url = config.url || "";
  const isPublicRoute =
    url.startsWith("/public/") ||
    url.startsWith("public/") ||
    url.startsWith("/campaigns/public") ||
    url.startsWith("campaigns/public") ||
    url.startsWith("/organizations/public") ||
    url.startsWith("organizations/public") ||
    url.startsWith("/kiddoush/public") ||
    url.startsWith("kiddoush/public");
  if (
    impersonate &&
    !url.startsWith("/owner/") &&
    !url.startsWith("owner/") &&
    !isPublicRoute
  ) {
    config.params = { ...(config.params || {}), organization_id: impersonate };
  }
  return config;
});

// ─── Retry automatique avec backoff exponentiel ─────────────────────────────
// Pourquoi: Railway met le backend en veille (cold start) → la première
// requête après ~30 min d'inactivité peut prendre 10-15s ou échouer.
// On retry automatiquement sur les erreurs réseau + 502/503/504 (transient).
// Limite à 3 tentatives pour ne pas bloquer l'utilisateur trop longtemps.
const RETRY_DELAYS_MS = [1000, 3000, 5000]; // 1s, 3s, 5s entre tentatives

const shouldRetry = (error, attemptCount) => {
  if (attemptCount >= RETRY_DELAYS_MS.length) return false;
  const method = (error?.config?.method || "get").toLowerCase();
  // Retry GET only by default (idempotent). POST/PUT/DELETE peuvent créer des doublons.
  if (method !== "get") return false;
  // Pas de réponse = erreur réseau (timeout, DNS fail, cold start, etc.)
  if (!error.response) return true;
  // 502/503/504 = backend temporairement indisponible
  if ([502, 503, 504].includes(error.response.status)) return true;
  return false;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    if (!config) return Promise.reject(error);

    config.__retryCount = config.__retryCount || 0;

    if (shouldRetry(error, config.__retryCount)) {
      const delay = RETRY_DELAYS_MS[config.__retryCount];
      config.__retryCount += 1;
      // eslint-disable-next-line no-console
      console.warn(
        `[apiClient] Retry ${config.__retryCount}/${RETRY_DELAYS_MS.length} dans ${delay}ms — ${config.method?.toUpperCase()} ${config.url}`
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
      return apiClient(config);
    }

    return Promise.reject(error);
  }
);

// Normalize backend error messages — FastAPI/Pydantic may return
// `detail` as an array of validation objects, but UI code assumes a string.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;

    // ── Auto-logout on 403 "Compte désactivé" ──────────────────────────────
    // Only trigger on "Compte désactivé" (account IS disabled), NOT on
    // "désactiver" (the verb — e.g. "Impossible de désactiver le dernier admin")
    if (status === 403 && typeof detail === "string" && /compte\s+d[ée]sactiv[ée]/i.test(detail)) {
      // Cookie is cleared by backend logout response
      // Small delay so the current request can finish
      setTimeout(() => {
        window.location.href = "/login?reason=disabled";
      }, 300);
      return Promise.reject(error);
    }

    if (Array.isArray(detail)) {
      const msg = detail
        .map((d) => {
          if (typeof d === "string") return d;
          const field = Array.isArray(d.loc) ? d.loc.slice(1).join(".") : "";
          return field ? `${field} : ${d.msg}` : d.msg;
        })
        .filter(Boolean)
        .join(" · ");
      error.response.data.detail = msg || "Données invalides.";
    } else if (detail && typeof detail === "object") {
      error.response.data.detail =
        detail.msg || JSON.stringify(detail);
    }
    return Promise.reject(error);
  }
);

export const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

export const formatAmount = (value) => {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(value));
};

// Format euros AVEC décimales — pour les petits montants (coûts SMS, frais Stripe,
// commissions) où couper à 0 décimale fait afficher "0 €" pour 0,05 € → confus.
export const formatAmountPrecise = (value, digits = 2) => {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value));
};
